export const EMPTY_APPRAISAL_FILTERS = {
  campaign: null,
  designation: null,
  department: null,
  month: null,
  quarter: null,
  pafStatus: null,
  supervisor: null,
};

export const SORT_COLUMN_LABELS = {
  name: "Employee Name",
  appraiser_name: "Appraiser Name",
  date: "Created Date",
  appraisal_form_name: "Appraisal Form Name",
  department: "Department",
  campaign: "Campaign",
  month: "Month",
  status: "Status",
};

const SORT_COLUMN_TO_URL = {
  name: "employee_name",
  appraiser_name: "appraiser_name",
  date: "created_date",
  appraisal_form_name: "appraisal_form_name",
  department: "department",
  campaign: "campaign",
  month: "month",
  status: "status",
};

const SORT_URL_TO_COLUMN = Object.fromEntries(
  Object.entries(SORT_COLUMN_TO_URL).map(([columnId, urlKey]) => [urlKey, columnId])
);

const FILTER_CHIP_LABELS = {
  campaign: "Campaign",
  designation: "Designation",
  department: "Department",
  month: "Month",
  quarter: "Quarter",
  pafStatus: "Status",
  supervisor: "Supervisor",
};

export const formatStatusLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

export function sortToUrlParam(sort) {
  if (!sort?.columnId || !sort?.direction) return null;
  const urlKey = SORT_COLUMN_TO_URL[sort.columnId];
  if (!urlKey) return null;
  return { sortBy: urlKey, sortDir: sort.direction };
}

export function sortFromUrlParams(searchParams) {
  const sortBy = searchParams.get("sortBy");
  const sortDir = searchParams.get("sortDir");
  if (!sortBy || !sortDir) return null;
  const columnId = SORT_URL_TO_COLUMN[sortBy];
  if (!columnId) return null;
  if (sortDir !== "asc" && sortDir !== "desc") return null;
  return { columnId, direction: sortDir };
}

export function filtersToApiShape(filters) {
  if (!filters) return {};
  const result = {};
  if (filters.campaign?.value) result.campaign = filters.campaign.value;
  if (filters.designation?.value) result.designation = filters.designation.value;
  if (filters.department?.value) result.department = filters.department.value;
  if (filters.month?.value) result.month = filters.month.value;
  if (filters.quarter?.value) result.quarter = filters.quarter.value;
  if (filters.pafStatus?.value) result.pafStatus = filters.pafStatus.value;
  if (filters.supervisor?.value) result.supervisor = filters.supervisor.value;
  return result;
}

export function hasActiveFilters(filters) {
  if (!filters) return false;
  return Object.values(filters).some((entry) => entry?.value);
}

export function parseSearchParamsToFilters(searchParams, labelResolver = {}) {
  const next = { ...EMPTY_APPRAISAL_FILTERS };

  const assign = (key, paramName, formatLabel) => {
    const raw = searchParams.get(paramName);
    if (!raw) return;
    const label =
      labelResolver[key]?.(raw) ||
      (typeof formatLabel === "function" ? formatLabel(raw) : raw);
    next[key] = { value: raw, label };
  };

  assign("campaign", "campaign");
  assign("designation", "designation");
  assign("department", "department");
  assign("month", "month");
  assign("quarter", "quarter");
  assign("pafStatus", "status", formatStatusLabel);
  assign("supervisor", "supervisor");

  return next;
}

export function buildSearchParamsFromState({ filters, sort, search }) {
  const params = new URLSearchParams();

  if (filters?.campaign?.value) params.set("campaign", String(filters.campaign.value));
  if (filters?.designation?.value) params.set("designation", String(filters.designation.value));
  if (filters?.department?.value) params.set("department", String(filters.department.value));
  if (filters?.month?.value) params.set("month", String(filters.month.value));
  if (filters?.quarter?.value) params.set("quarter", String(filters.quarter.value));
  if (filters?.pafStatus?.value) params.set("status", String(filters.pafStatus.value));
  if (filters?.supervisor?.value) params.set("supervisor", String(filters.supervisor.value));

  const sortParams = sortToUrlParam(sort);
  if (sortParams) {
    params.set("sortBy", sortParams.sortBy);
    params.set("sortDir", sortParams.sortDir);
  }

  if (search && String(search).trim()) {
    params.set("q", String(search).trim());
  }

  return params;
}

export function buildActiveFilterChips(filters, sort) {
  const chips = [];

  Object.entries(FILTER_CHIP_LABELS).forEach(([key, prefix]) => {
    const entry = filters?.[key];
    if (entry?.value) {
      chips.push({
        id: `filter-${key}`,
        type: "filter",
        key,
        label: `${prefix}: ${entry.label || entry.value}`,
      });
    }
  });

  if (sort?.columnId && sort?.direction) {
    const columnLabel = SORT_COLUMN_LABELS[sort.columnId] || sort.columnId;
    const arrow = sort.direction === "asc" ? "↑" : "↓";
    chips.push({
      id: "sort",
      type: "sort",
      label: `Sort: ${columnLabel} ${arrow}`,
    });
  }

  return chips;
}

export function resolveFilterLabels(filters, {
  campaigns = [],
  designations = [],
  departments = [],
} = {}) {
  if (!filters) return EMPTY_APPRAISAL_FILTERS;

  const findLabel = (list, value) => {
    const match = list.find((item) => String(item.id) === String(value));
    if (!match) return String(value);
    return match.name || match.client_name || match.campaign_name || match.designation_name || match.department_name || String(value);
  };

  const next = { ...filters };

  if (next.campaign?.value) {
    next.campaign = {
      value: next.campaign.value,
      label: findLabel(campaigns, next.campaign.value),
    };
  }
  if (next.designation?.value) {
    next.designation = {
      value: next.designation.value,
      label: findLabel(designations, next.designation.value),
    };
  }
  if (next.department?.value) {
    next.department = {
      value: next.department.value,
      label: findLabel(departments, next.department.value),
    };
  }
  if (next.pafStatus?.value && !next.pafStatus.label) {
    next.pafStatus = {
      value: next.pafStatus.value,
      label: formatStatusLabel(next.pafStatus.value),
    };
  }

  return next;
}
